import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from '@models/Core/User';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { BusService } from '@services/bus.service';
import { EventsService } from '@services/events.service';
import { SearchService } from '@services/search.service';

export abstract class Suggestion {
  private _segments: { t: string; m: boolean }[] = [];

  constructor(
    private _text: string,
    private _type: string,
    protected context: any,
  ) {
    const regex = new RegExp(
      `(${context.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'i',
    );
    this._segments = _text.split(regex).map(segment => ({
      t: segment,
      m: regex.test(segment),
    }));
  }

  public get text(): string {
    return this._text;
  }
  public get type(): string {
    return this._type;
  }

  public get segments(): { t: string; m: boolean }[] {
    return this._segments;
  }

  public abstract open(event);
}

export class IdeaSuggestion extends Suggestion {
  constructor(
    private idea: Idea,
    private session: Session,
    protected override context: any,
  ) {
    super(idea.title, 'idea', context);
  }

  public open(event) {
    event.stopPropagation();
    this.context.bus.publish(
      this.context.events.requested.data.session.idea.singleWithSession,
      this.idea._id,
      this.session._id,
    );
  }
}

export class SessionSuggestion extends Suggestion {
  constructor(
    private session: Session,
    protected override context: any,
  ) {
    super(session.config.title, 'topic', context);
  }

  public open(event) {
    event.stopPropagation();
    this.context.router.navigate(['/topics/' + this.session._id + '/']);
    this.context.searchbox.nativeElement.blur();
  }
}

export class UserSuggestion extends Suggestion {
  constructor(
    private user: User,
    protected override context: any,
  ) {
    super(
      (user.first_name ? user.first_name + ' ' : '') +
        user.name +
        (user.organization ? ` (${user.organization})` : ''),
      'user',
      context,
    );
  }

  public open(event) {
    event.stopPropagation();
    this.context.router.navigate(['/profile/' + this.user._id]);
    this.context.searchbox.nativeElement.blur();
  }
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'searchbar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './searchbar.component.html',
  styleUrls: ['./searchbar.component.scss'],
})
export class SearchbarComponent implements OnInit {
  @ViewChild('searchbox', { static: true }) searchbox: ElementRef;
  @Input() mobile = false;
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();

  suggestions: Suggestion[] = [];
  query = '';

  suggestionTimeout: any = undefined;

  _focused = false;

  constructor(
    private bus: BusService,
    private events: EventsService,
    private router: Router,
    private route: ActivatedRoute,
    private searchService: SearchService,
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params.q) {
        this.query = params.q;
      }
    });
  }

  suggest() {
    this._focused = true;

    if (this.query && this.query.length > 0) {
      if (this.suggestionTimeout) {
        clearTimeout(this.suggestionTimeout);
      }

      this.suggestionTimeout = setTimeout(() => {
        this.searchService.suggest(this.query).subscribe((response: any) => {
          this.suggestions = [];
          response.result.forEach(entry => {
            if (entry.type === 'topic') {
              this.suggestions.push(new SessionSuggestion(entry.topic, this));
            } else if (entry.type === 'idea') {
              const session = response.ideaTopics[entry.session];
              if (session) {
                this.suggestions.push(
                  new IdeaSuggestion(entry.idea, session, this),
                );
              }
            } else if (entry.type === 'user') {
              this.suggestions.push(new UserSuggestion(entry.user, this));
            }
          });
        });
      }, 200);
    }
  }

  focus() {
    this._focused = true;
    this.searchbox.nativeElement.focus();
  }

  unfocus() {
    setTimeout(() => {
      if (document.activeElement !== this.searchbox.nativeElement) {
        this._focused = false;
      }
    }, 200);
  }

  get focused(): boolean {
    return this._focused;
  }

  search(event?) {
    if (this.query && this.query.length > 0) {
      if (event) {
        event.stopPropagation();
      }
      this.searchbox.nativeElement.blur();
      this.router.navigate(['/search'], {
        queryParams: {
          q: this.query,
        },
      });
    }
  }

  clear() {
    if (this.query.length > 0) {
      this.query = '';
    } else {
      this.closed.emit();
    }
  }

  checkSubmit(event) {
    if (event.keyCode === 13) {
      this.search();
    }
  }
}
